import React from 'react';
import { useAuth } from '@/context/AuthContext';
import { LogOut, User, Menu } from 'lucide-react';

interface HeaderProps {
  onMobileMenuToggle?: () => void;
}

export const Header: React.FC<HeaderProps> = ({ onMobileMenuToggle }) => {
  const { profile, user, role, signOut } = useAuth();

  const roleColorMap = {
    owner: 'bg-emerald-100 text-brand-green border-brand-green/30',
    manager: 'bg-blue-100 text-blue-800 border-blue-300',
    staff: 'bg-gray-100 text-gray-800 border-gray-300',
  };

  const badgeStyle = role ? roleColorMap[role] : roleColorMap.staff;

  return (
    <header className="h-16 bg-white border-b border-brand-border sticky top-0 z-30 px-4 sm:px-6 flex items-center justify-between shadow-xs">
      <div className="flex items-center space-x-3">
        {/* Mobile menu trigger button */}
        <button
          onClick={onMobileMenuToggle}
          className="md:hidden p-2 rounded-md text-brand-black hover:bg-brand-bg transition-colors"
          aria-label="Open Navigation Menu"
        >
          <Menu className="w-5 h-5" />
        </button>

        <div className="flex items-center space-x-2">
          <span className="text-xs font-semibold px-2 py-0.5 rounded bg-brand-bg text-brand-black border border-brand-border hidden sm:inline-block">
            PKR (Rs)
          </span>
          <span className="text-xs text-brand-muted hidden md:inline-block">
            Asia/Karachi
          </span>
        </div>
      </div>

      {/* User info and actions */}
      <div className="flex items-center space-x-4">
        {/* Profile Card */}
        <div className="flex items-center space-x-3 border-r border-brand-border pr-4">
          <div className="w-8 h-8 rounded-full bg-brand-green text-white flex items-center justify-center font-bold text-xs">
            {profile?.full_name ? profile.full_name.charAt(0).toUpperCase() : <User className="w-4 h-4" />}
          </div>
          <div className="text-left hidden sm:block">
            <p className="text-xs font-bold text-brand-black leading-tight">
              {profile?.full_name || user?.email || 'Authenticated Staff'}
            </p>
            <p className="text-[11px] text-brand-muted leading-tight">
              {user?.email}
            </p>
          </div>
          <span
            className={`text-[10px] font-bold tracking-wider uppercase px-2 py-0.5 rounded border ${badgeStyle}`}
          >
            {role || 'staff'}
          </span>
        </div>

        {/* Logout Button */}
        <button
          onClick={() => signOut()}
          className="inline-flex items-center space-x-1.5 px-3 py-1.5 rounded-md text-xs font-medium text-brand-red hover:bg-brand-red-light transition-colors border border-transparent hover:border-brand-red/20"
          title="Sign out of account"
        >
          <LogOut className="w-3.5 h-3.5" />
          <span className="hidden sm:inline">Logout</span>
        </button>
      </div>
    </header>
  );
};
